import { CAMPOS_DO_PLANO } from '../../../lib/glossario'
import type { CampoDoPlano } from '../../../types/gmud'
import type { FormularioNovaGmud } from './esquema'

/**
 * O rótulo do número do chamado, num lugar só.
 *
 * O campo aparece na etapa 1 e a falta dele aparece na etapa 3. Escrito duas vezes, um dos dois
 * seria renomeado sozinho e a conferência passaria a cobrar um campo com outro nome — que, para
 * quem não é de TI, é um campo diferente.
 */
export const ROTULO_DO_CHAMADO = 'Número do chamado'

/**
 * Os seis artefatos que `PlanoDeMudanca.pendencias()` cobra, na mesma ordem.
 *
 * Note quem NÃO está aqui: "o que precisa estar pronto antes" e "o que a mudança entrega". Os oito
 * campos do plano são pedidos no formulário, mas só seis bloqueiam a ida ao comitê — são os quatro
 * do ANEXO XXI (procedimentos, contingência, cronograma e recursos, estes dois ainda fora da tela)
 * mais o plano de volta e a evidência de homologação do item 4.62. Cobrar os oito faria a tela
 * inventar exigência que o contrato não faz.
 */
export const ARTEFATOS_EXIGIDOS: readonly CampoDoPlano[] = [
  'justificativa',
  'procedimentosImplementacao',
  'planoRollback',
  'planoContingencia',
  'testeVolta',
  'evidenciaTesteDescricao',
]

export interface Pendencia {
  campo: keyof FormularioNovaGmud
  rotulo: string
}

const ROTULO_DO_CAMPO = new Map<string, string>(CAMPOS_DO_PLANO.map((campo) => [campo.campo, campo.rotulo]))

/**
 * O que ainda falta para esta solicitação poder ir ao comitê.
 *
 * Espelha `Gmud.verificarInvariantes` e `PlanoDeMudanca.pendencias()`, inclusive na ordem e no
 * detalhe de que o número do chamado é cobrado também quando a mudança vem de projeto — a
 * invariante não olha o tipo. O que muda é a palavra: o backend responde em linguagem técnica
 * ("Plano de volta (rollback)") porque a mensagem dele serve a qualquer cliente da API; aqui o
 * nome tem de ser o MESMO que está escrito na caixa que a pessoa precisa voltar e preencher.
 *
 * Esta lista é orientação, não autorização: quem recusa a transição continua sendo o domínio, e a
 * lista que vale é a que `POST /gmuds/{id}/transicoes` devolve. Adiantá-la aqui evita que o
 * usuário salve o rascunho achando que acabou.
 *
 * @param valores o que já foi preenchido no formulário
 * @returns as pendências, na ordem em que o domínio as cobra
 */
export function pendenciasParaOComite(valores: Partial<FormularioNovaGmud>): Pendencia[] {
  const faltando: Pendencia[] = ARTEFATOS_EXIGIDOS.filter((campo) => !valores[campo]?.trim()).map((campo) => ({
    campo,
    rotulo: ROTULO_DO_CAMPO.get(campo) ?? campo,
  }))

  if (!valores.chamadoNumero?.trim()) {
    faltando.push({ campo: 'chamadoNumero', rotulo: ROTULO_DO_CHAMADO })
  }
  return faltando
}

/**
 * Quantos dos artefatos obrigatórios já têm conteúdo.
 *
 * É o "3 de 6" da etapa 2. Um número que sobe enquanto se escreve diz o que uma lista de campos
 * vazios não diz: quanto falta para acabar.
 *
 * @param valores o que já foi preenchido no formulário
 * @returns quantos dos seis artefatos exigidos estão preenchidos
 */
export function artefatosPreenchidos(valores: Partial<FormularioNovaGmud>): number {
  return ARTEFATOS_EXIGIDOS.filter((campo) => Boolean(valores[campo]?.trim())).length
}
